import React, { useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { useLanguage } from '../../i18n/LanguageContext';
import { useActiveWorkout } from '../../hooks/useActiveWorkout';

interface ActiveWorkoutViewProps {
  open: boolean;
  onClose: () => void;
  onSessionLogged: () => void;
}

export const ActiveWorkoutView: React.FC<ActiveWorkoutViewProps> = ({
  open,
  onClose,
  onSessionLogged,
}) => {
  const { t } = useLanguage();
  const { activeWorkout, markSetDone, updateSet, updateNotes, finishWorkout, clearWorkout } =
    useActiveWorkout();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [abandonConfirmOpen, setAbandonConfirmOpen] = useState(false);
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);

  if (!activeWorkout) return null;

  const totalSets = activeWorkout.exercises.reduce((sum, ex) => sum + ex.sets_data.length, 0);
  const completedSets = activeWorkout.exercises.reduce(
    (sum, ex) => sum + ex.sets_data.filter((s) => s.completed).length,
    0
  );
  const remainingSets = totalSets - completedSets;
  const allDone = remainingSets === 0;

  const startedTime = new Date(activeWorkout.startedAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  const handleFinishClick = () => {
    if (!allDone) {
      setFinishConfirmOpen(true);
    } else {
      submitWorkout();
    }
  };

  const submitWorkout = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await finishWorkout();
      onSessionLogged();
      onClose();
    } catch (err: any) {
      setError(err.message ?? 'Failed to log session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAbandonConfirmed = () => {
    clearWorkout();
    setAbandonConfirmOpen(false);
    onClose();
  };

  const progressValue = totalSets > 0 ? (completedSets / totalSets) * 100 : 0;

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen>
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar>
            <IconButton edge="start" onClick={onClose} aria-label="minimize">
              <CloseIcon />
            </IconButton>
            <Box sx={{ flex: 1, ml: 1 }}>
              <Typography variant="h6" noWrap>
                {activeWorkout.templateName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('programs.startedAt')} {startedTime}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleFinishClick}
              disabled={submitting}
              endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('programs.finishWorkout')}
            </Button>
          </Toolbar>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{ height: 4 }}
          />
        </AppBar>

        <DialogContent sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('programs.setsDone')
              .replace('{done}', String(completedSets))
              .replace('{total}', String(totalSets))}
          </Typography>

          {activeWorkout.exercises.map((ex, exIdx) => (
            <Box key={ex.program_exercise_id} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {ex.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ex.sets}×{ex.reps} @ {ex.current_weight_kg} kg
                </Typography>
              </Box>

              {ex.sets_data.map((set) => (
                <Box
                  key={set.set_index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                    opacity: set.completed ? 0.7 : 1,
                  }}
                >
                  <Typography variant="body2" sx={{ minWidth: 40, color: 'text.secondary' }}>
                    {t('programs.sets').slice(0, 3)} {set.set_index + 1}
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    label="kg"
                    value={set.weight_kg}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!isNaN(val) && val >= 0) {
                        updateSet(ex.program_exercise_id, set.set_index, set.reps_completed, val);
                      }
                    }}
                    slotProps={{ htmlInput: { min: 0, step: 2.5 } }}
                    sx={{ width: 80 }}
                  />
                  <TextField
                    type="number"
                    size="small"
                    label={t('programs.reps')}
                    value={set.reps_completed}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 0) {
                        updateSet(ex.program_exercise_id, set.set_index, val, set.weight_kg);
                      }
                    }}
                    slotProps={{ htmlInput: { min: 0, step: 1 } }}
                    sx={{ width: 80 }}
                  />
                  {set.completed ? (
                    <Button
                      size="small"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => markSetDone(ex.program_exercise_id, set.set_index)}
                      sx={{ minWidth: 110 }}
                    >
                      {t('programs.markDone')}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => markSetDone(ex.program_exercise_id, set.set_index)}
                      sx={{ minWidth: 110 }}
                    >
                      {t('programs.markDone')}
                    </Button>
                  )}
                </Box>
              ))}

              {exIdx < activeWorkout.exercises.length - 1 && <Divider sx={{ mt: 1 }} />}
            </Box>
          ))}

          <TextField
            label={t('programs.notesOptional')}
            multiline
            rows={2}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            value={activeWorkout.notes}
            onChange={(e) => updateNotes(e.target.value)}
          />
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button
            color="error"
            onClick={() => setAbandonConfirmOpen(true)}
            disabled={submitting}
          >
            {t('programs.abandonWorkout')}
          </Button>
          <Button
            variant="contained"
            onClick={handleFinishClick}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : t('programs.finishWorkout')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Abandon confirmation */}
      <Dialog open={abandonConfirmOpen} onClose={() => setAbandonConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('programs.abandonWorkout')}</DialogTitle>
        <DialogContent>
          <Typography>{t('programs.abandonConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbandonConfirmOpen(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="contained" color="error" onClick={handleAbandonConfirmed}>
            {t('programs.abandonWorkout')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Finish incomplete confirmation */}
      <Dialog open={finishConfirmOpen} onClose={() => setFinishConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('programs.finishWorkout')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('programs.finishIncomplete').replace('{remaining}', String(remainingSets))}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinishConfirmOpen(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setFinishConfirmOpen(false);
              submitWorkout();
            }}
          >
            {t('programs.finishWorkout')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
